import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useWhatsAppLead } from '@/hooks/useWhatsAppLead';
import { trackEvent } from '@/hooks/useTracking';

const TICKER_ITEMS = [
  { name: 'Ahmet Kuaför', city: 'Ankara', time: '3 dk önce', icon: 'ri-scissors-line' },
  { name: 'Selin Güzellik Merkezi', city: 'İstanbul', time: '11 dk önce', icon: 'ri-heart-line' },
  { name: 'Mert Diş Kliniği', city: 'İzmir', time: '18 dk önce', icon: 'ri-hospital-line' },
  { name: 'Özlem Pilates Stüdyo', city: 'Bursa', time: '27 dk önce', icon: 'ri-run-line' },
  { name: 'Kadir Oto Servis', city: 'Antalya', time: '34 dk önce', icon: 'ri-car-line' },
  { name: 'Pınar Nail Art', city: 'Eskişehir', time: '41 dk önce', icon: 'ri-brush-line' },
  { name: 'Tarık Hukuk Bürosu', city: 'İstanbul', time: '52 dk önce', icon: 'ri-briefcase-line' },
  { name: 'Zeynep Psikoloji', city: 'Ankara', time: '1 sa önce', icon: 'ri-mental-health-line' },
  { name: 'Emre Fotoğrafçılık', city: 'Antalya', time: '1 sa önce', icon: 'ri-camera-line' },
  { name: 'Hande Diyetisyen', city: 'İzmir', time: '2 sa önce', icon: 'ri-leaf-line' },
];

function LiveTicker() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <div className="w-full border-t border-white/5 overflow-hidden" style={{ background: 'rgba(10,10,15,0.7)', backdropFilter: 'blur(12px)' }}>
      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          animation: ticker-scroll 38s linear infinite;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="flex items-center h-11">
        {/* Left badge — fixed */}
        <div className="flex items-center gap-2 px-4 h-full border-r border-white/8 flex-shrink-0" style={{ background: 'rgba(220,38,38,0.12)' }}>
          <span className="relative flex items-center justify-center w-2.5 h-2.5 flex-shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-70 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-400" />
          </span>
          <span className="text-red-400 text-[11px] font-bold tracking-widest uppercase whitespace-nowrap">Canlı</span>
        </div>

        {/* Scrolling track */}
        <div className="overflow-hidden flex-1">
          <div className="ticker-track">
            {doubled.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 px-5 whitespace-nowrap">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-green-500/15 border border-green-500/25 flex-shrink-0">
                  <i className={`${item.icon} text-[10px] text-green-400`} />
                </span>
                <span className="text-[12px] text-gray-300">
                  <span className="text-white font-semibold">{item.name}</span>
                  <span className="text-gray-500">, {item.city}</span>
                  <span className="text-green-400 font-medium ml-1">sisteme geçti</span>
                </span>
                <span className="text-gray-600 text-[11px] ml-1">{item.time}</span>
                <span className="text-white/10 ml-3 text-base select-none">·</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right label */}
        <div className="flex items-center gap-1.5 px-4 h-full border-l border-white/8 flex-shrink-0">
          <i className="ri-arrow-right-line text-gold text-xs" />
          <span className="text-gold text-[11px] font-semibold whitespace-nowrap">Sen de katıl</span>
        </div>
      </div>
    </div>
  );
}

function useLiveCounter(target: number, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const counterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (counterRef.current) observer.observe(counterRef.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = target / steps;
    const interval = duration / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, interval);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return { count, counterRef };
}

function useCounter(target: number, duration = 2200, started = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    const steps = 70;
    const increment = target / steps;
    const interval = duration / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, interval);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return count;
}

const HEADLINE_VARIANTS = [
  { prefix: 'Web Siteniz Var Ama', highlight: 'Müşteri Getirmiyor mu?' },
  { prefix: 'Web Siteniz', highlight: 'Yok mu?' },
];

function CyclingHeadline() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [animState, setAnimState] = useState<'visible' | 'leaving' | 'entering'>('visible');

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimState('leaving');
      setTimeout(() => {
        setActiveIdx((prev) => (prev + 1) % HEADLINE_VARIANTS.length);
        setAnimState('entering');
        setTimeout(() => setAnimState('visible'), 400);
      }, 350);
    }, 3800);
    return () => clearInterval(interval);
  }, []);

  const variant = HEADLINE_VARIANTS[activeIdx];

  const style: CSSProperties = {
    transition: 'opacity 0.35s ease, transform 0.35s ease',
    opacity: animState === 'leaving' ? 0 : 1,
    transform: animState === 'leaving'
      ? 'translateY(-12px)'
      : animState === 'entering'
        ? 'translateY(10px)'
        : 'translateY(0)',
  };

  return (
    <div className="flex flex-col gap-3 min-h-[7rem] md:min-h-[8rem] justify-center">
      <h1
        className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-tight tracking-tight text-center sm:text-left"
        style={style}
      >
        {variant.prefix}{' '}
        <span className="text-gold-gradient">{variant.highlight}</span>
      </h1>
      {/* Dot indicators */}
      <div className="flex items-center gap-2 justify-center sm:justify-start">
        {HEADLINE_VARIANTS.map((v, i) => (
          <span
            key={v.highlight}
            className="block rounded-full transition-all duration-500"
            style={{
              width: i === activeIdx ? '20px' : '6px',
              height: '6px',
              background: i === activeIdx ? '#f0b429' : 'rgba(240,180,41,0.3)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { count, counterRef } = useLiveCounter(34, 1800);
  const customerCount = useCounter(128, 2200, count > 0);
  const { openModal } = useWhatsAppLead();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.add('opacity-0');
    const timeout = setTimeout(() => {
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 100);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at 20% 50%, rgba(240,180,41,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(0,212,255,0.06) 0%, transparent 50%), linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 100%)',
      }}
    >
      {/* Animated grid background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(240,180,41,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(240,180,41,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-10 w-80 h-80 rounded-full opacity-5 animate-float" style={{ background: 'radial-gradient(circle, #f0b429, transparent)' }} />
      <div className="absolute bottom-1/4 right-10 w-64 h-64 rounded-full opacity-5 animate-float" style={{ background: 'radial-gradient(circle, #00d4ff, transparent)', animationDelay: '3s' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full pt-24 pb-24">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

          {/* LEFT CONTENT */}
          <div ref={ref} className="flex-1 text-center lg:text-left" style={{ transform: 'translateY(20px)' }}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 glass-gold px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse-dot" />
              <span className="text-gold text-sm font-semibold tracking-wide">Dijital Satış Platformu</span>
            </div>

            {/* Main Headline with Logo */}
            <div className="flex flex-col sm:flex-row items-center lg:items-start gap-5 mb-5">              {/* Logo next to headline */}
              <div className="flex-shrink-0 flex items-center justify-center">
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-2xl blur-xl opacity-40"
                    style={{ background: 'radial-gradient(circle, #f0b429, transparent)' }}
                  />
                  <img
                    src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/caaea5a1b17e64d200d3c08d14d4a01f.png"
                    alt="Smart Hizmet Platform"
                    className="relative w-24 h-24 md:w-28 md:h-28 object-contain"
                    style={{ filter: 'drop-shadow(0 0 18px rgba(240,180,41,0.55))' }}
                  />
                </div>
              </div>

              {/* Headline text — cycling between two pain points */}
              <CyclingHeadline />
            </div>

            {/* Sub-headline */}
            <p className="text-lg md:text-xl text-neon font-medium mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Size müşteri getiren satış odaklı dijital platform kuruyoruz.
            </p>

            {/* Bullet points */}
            <ul className="space-y-4 mb-10 max-w-md mx-auto lg:mx-0">
              {[
                { icon: 'ri-user-received-2-line', text: 'Reklamdan gelen kişiler direkt size ulaşır' },
                { icon: 'ri-whatsapp-line', text: 'WhatsApp ve formlar ile anında dönüşüm' },
                { icon: 'ri-rocket-2-line', text: 'Saatler içinde yayında' },
              ].map((item) => (
                <li key={item.text} className="flex items-center gap-3 text-gray-200 text-base font-medium">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-gold/15 border border-gold/30 text-gold flex-shrink-0">
                    <i className={`${item.icon} text-sm`} />
                  </span>
                  {item.text}
                </li>
              ))}
            </ul>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={() => { trackEvent('button_click', 'Demo Talep Et', 'Hero'); openModal('Hero — Demo Talep Et'); }}
                className="btn-gold px-8 py-4 rounded-xl text-base font-bold cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-whatsapp-line" />
                Demo Talep Et
              </button>
              <button
                onClick={() => { trackEvent('button_click', 'WhatsApp\'tan Yaz', 'Hero'); openModal('Hero — WhatsApp\'tan Yaz'); }}
                className="btn-outline-neon px-8 py-4 rounded-xl text-base cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-whatsapp-line" />
                WhatsApp&apos;tan Yaz
              </button>
            </div>

            {/* Live Social Proof Counter — çift stat */}
            <div ref={counterRef} className="mt-8 mb-2">
              <div className="flex flex-col sm:flex-row items-stretch gap-3">

                {/* Stat 1 — İşletme sayısı */}
                <div
                  className="flex items-center gap-3 rounded-2xl px-5 py-3 border border-green-500/20 flex-1"
                  style={{ background: 'rgba(16,185,129,0.06)', backdropFilter: 'blur(10px)' }}
                >
                  <span className="relative flex items-center justify-center w-3 h-3 flex-shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 leading-none mb-0.5">Bu ay sisteme geçen</p>
                    <p className="text-sm font-semibold text-gray-200 whitespace-nowrap">
                      <span className="text-green-400 font-extrabold text-lg tabular-nums">{count}</span>
                      {' '}işletme
                    </p>
                  </div>
                  {/* Avatars */}
                  <div className="flex -space-x-2 flex-shrink-0 ml-auto">
                    {[
                      'https://readdy.ai/api/search-image?query=professional%20smiling%20woman%20business%20owner%20headshot%20warm%20neutral%20background%20close-up%20portrait%20photography%20realistic&width=32&height=32&seq=av1&orientation=squarish',
                      'https://readdy.ai/api/search-image?query=young%20professional%20man%20entrepreneur%20smiling%20headshot%20clean%20light%20background%20portrait%20photography%20realistic&width=32&height=32&seq=av2&orientation=squarish',
                      'https://readdy.ai/api/search-image?query=professional%20woman%20small%20business%20owner%20smiling%20headshot%20soft%20background%20portrait%20photography%20realistic&width=32&height=32&seq=av3&orientation=squarish',
                    ].map((src, i) => (
                      <img key={i} src={src} alt="İşletme" className="w-7 h-7 rounded-full border-2 object-cover object-top" style={{ borderColor: 'rgba(16,185,129,0.4)' }} />
                    ))}
                    <div className="w-7 h-7 rounded-full border-2 border-green-500/40 bg-green-900/40 flex items-center justify-center text-[9px] font-bold text-green-400 flex-shrink-0">
                      +{count > 3 ? count - 3 : ''}
                    </div>
                  </div>
                </div>

                {/* Stat 2 — Üretilen müşteri */}
                <div
                  className="flex items-center gap-3 rounded-2xl px-4 md:px-5 py-3 border border-gold/20 flex-1"
                  style={{ background: 'rgba(240,180,41,0.06)', backdropFilter: 'blur(10px)' }}
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-gold/15 border border-gold/25 flex-shrink-0">
                    <i className="ri-user-add-line text-gold text-base" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400 leading-none mb-0.5">Son 7 günde üretilen</p>
                    <p className="text-sm font-semibold text-gray-200 whitespace-nowrap">
                      <span className="text-gold font-extrabold text-lg tabular-nums">{customerCount}</span>
                      {' '}yeni müşteri
                    </p>
                  </div>
                  <div className="hidden sm:flex flex-shrink-0">
                    <div className="flex items-center gap-1 text-gold text-[11px] font-bold whitespace-nowrap">
                      <i className="ri-arrow-up-line text-xs" />
                      <span>%340 dönüşüm</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-10 justify-center lg:justify-start">
              <div className="flex -space-x-2">
                {['https://readdy.ai/api/search-image?query=professional%20woman%20business%20owner%20headshot%20neutral%20background%20portrait&width=40&height=40&seq=1&orientation=squarish',
                  'https://readdy.ai/api/search-image?query=professional%20man%20entrepreneur%20headshot%20neutral%20background%20portrait&width=40&height=40&seq=2&orientation=squarish',
                  'https://readdy.ai/api/search-image?query=young%20professional%20woman%20headshot%20clean%20background%20portrait&width=40&height=40&seq=3&orientation=squarish',
                ].map((src, i) => (
                  <img key={i} src={src} alt="Müşteri" className="w-9 h-9 rounded-full border-2 border-dark object-top object-cover" />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-gold text-sm font-bold">
                  <i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" />
                </div>
                <p className="text-gray-400 text-xs mt-0.5">100+ işletme memnun</p>
              </div>
            </div>
          </div>

          {/* RIGHT VISUAL */}
          <div className="flex-1 max-w-xl w-full">
            <div className="relative animate-float">

              {/* Floating badge left — ortalanmış, iki kart arasında */}
              <div
                className="absolute -left-5 top-1/2 -translate-y-1/2 z-20 rounded-xl px-3 py-2.5 flex flex-col items-center gap-0.5"
                style={{
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.25) 0%, rgba(16,185,129,0.1) 100%)',
                  border: '1px solid rgba(16,185,129,0.4)',
                  boxShadow: '0 0 18px rgba(16,185,129,0.3)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <i className="ri-arrow-up-line text-green-400 text-sm" />
                <div className="text-green-400 font-black text-base leading-none">%340</div>
                <div className="text-gray-500 text-[9px] font-semibold">ROI</div>
              </div>

              {/* BEFORE card */}
              <div
                className="rounded-2xl p-5 mb-1 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(10,10,15,0.9) 100%)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  boxShadow: '0 0 30px rgba(239,68,68,0.1), inset 0 1px 0 rgba(239,68,68,0.15)',
                }}
              >
                {/* Noise texture overlay */}
                <div className="absolute inset-0 rounded-2xl opacity-[0.03]"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className="text-xs text-red-300/80 font-semibold tracking-wide uppercase">Şu Anki Siteniz</span>
                    </div>
                    <span className="text-[10px] text-red-400/70 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">Düşük Performans</span>
                  </div>

                  {/* Bar chart - flat/poor */}
                  <div className="flex items-end gap-1.5 h-20 mb-3 px-1">
                    {[22, 38, 18, 44, 12, 28, 10].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t-sm relative overflow-hidden" style={{ height: `${h * 1.8}px` }}>
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(239,68,68,0.55) 0%, rgba(239,68,68,0.2) 100%)' }} />
                      </div>
                    ))}
                  </div>

                  {/* Flatline */}
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.5), transparent)' }} />
                    <span className="text-[10px] text-red-400/60 whitespace-nowrap">müşteri yok</span>
                    <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.5), transparent)' }} />
                  </div>

                  <div className="flex items-center justify-between text-xs mb-3">
                    <span className="text-gray-500">2,847 ziyaretçi</span>
                    <span className="text-red-400 font-bold">Dönüşüm: %0.2</span>
                  </div>

                  <div className="flex items-center gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-fill text-red-400 text-sm" />
                    </span>
                    <span className="text-xs text-gray-400">Ziyaretçi var ama kimse iletişime geçmiyor</span>
                  </div>
                </div>
              </div>

              {/* VS divider */}
              <div className="flex items-center gap-3 my-3 px-2">
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06))' }} />
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-black"
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e, #0a0a0f)',
                    border: '1px solid rgba(240,180,41,0.4)',
                    color: '#f0b429',
                    boxShadow: '0 0 12px rgba(240,180,41,0.3)',
                  }}
                >
                  VS
                </div>
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.06), transparent)' }} />
              </div>

              {/* AFTER card */}
              <div className="relative">
                {/* 42x badge — After kartına ait */}
                <div
                  className="absolute -top-4 -right-4 z-20 rounded-2xl px-4 py-2.5 text-center animate-badge-pulse"
                  style={{
                    background: 'linear-gradient(135deg, #f0b429 0%, #e67e22 100%)',
                  }}
                >
                  <div className="text-black font-black text-2xl leading-none">42x</div>
                  <div className="text-black/70 text-[10px] font-bold mt-0.5">Daha Fazla Lead</div>
                </div>

                <div
                  className="rounded-2xl p-5 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(240,180,41,0.1) 0%, rgba(10,10,15,0.95) 60%, rgba(16,185,129,0.08) 100%)',
                  border: '1px solid rgba(240,180,41,0.35)',
                  boxShadow: '0 0 40px rgba(240,180,41,0.18), 0 0 80px rgba(240,180,41,0.08), inset 0 1px 0 rgba(240,180,41,0.2)',
                }}
              >
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 pointer-events-none"
                  style={{ background: 'radial-gradient(circle, #f0b429, transparent)', transform: 'translate(30%, -30%)' }} />

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="relative flex w-2.5 h-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
                      </span>
                      <span className="text-xs text-green-300/90 font-semibold tracking-wide uppercase">Smart Platform</span>
                    </div>
                    <span className="text-[10px] text-green-400 bg-green-500/10 border border-green-500/25 px-2 py-0.5 rounded-full font-semibold">Canlı · Müşteri Geliyor</span>
                  </div>

                  {/* Growth bar chart */}
                  <div className="flex items-end gap-1.5 h-16 mb-4 px-1">
                    {[18, 32, 45, 38, 58, 72, 95].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t-sm relative overflow-hidden" style={{ height: `${h * 0.62}px` }}>
                        <div
                          className="absolute inset-0"
                          style={{
                            background: i >= 5
                              ? 'linear-gradient(180deg, #f0b429 0%, rgba(240,180,41,0.4) 100%)'
                              : 'linear-gradient(180deg, rgba(16,185,129,0.8) 0%, rgba(16,185,129,0.25) 100%)',
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Messages */}
                  <div className="space-y-2 mb-4">
                    {[
                      { name: 'Ayşe H.', initial: 'A', msg: 'Randevu almak istiyorum, müsait misiniz?', time: '2 dk', color: 'from-purple-500/30 to-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-300' },
                      { name: 'Mehmet K.', initial: 'M', msg: 'Fiyat bilgisi alabilir miyim?', time: '8 dk', color: 'from-gold/20 to-gold/5', border: 'border-gold/30', text: 'text-gold' },
                      { name: 'Fatma Y.', initial: 'F', msg: 'Bu hafta uygun saatiniz var mı?', time: '15 dk', color: 'from-green-500/25 to-green-500/5', border: 'border-green-500/30', text: 'text-green-400' },
                    ].map((msg) => (
                      <div key={msg.name} className="flex items-center gap-2.5 rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${msg.color} border ${msg.border} flex items-center justify-center ${msg.text} text-xs font-black flex-shrink-0`}>
                          {msg.initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-bold text-white">{msg.name}</span>
                            <span className="text-[10px] text-gray-600">{msg.time} önce</span>
                          </div>
                          <p className="text-[11px] text-gray-400 truncate">{msg.msg}</p>
                        </div>
                        <i className="ri-whatsapp-line text-green-500 text-base flex-shrink-0" />
                      </div>
                    ))}
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: '%8.4', label: 'Dönüşüm', color: 'text-gold', bg: 'rgba(240,180,41,0.08)', border: 'rgba(240,180,41,0.2)' },
                      { value: '+42', label: 'Bu Ay', color: 'text-green-400', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)' },
                      { value: '7/24', label: 'Aktif', color: 'text-neon', bg: 'rgba(0,212,255,0.08)', border: 'rgba(0,212,255,0.2)' },
                    ].map((s) => (
                      <div key={s.label} className="rounded-xl py-2.5 text-center" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
                        <div className={`${s.color} font-black text-lg leading-none`}>{s.value}</div>
                        <div className="text-gray-500 text-[10px] mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div> {/* closes relative wrapper for After card */}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-gray-500 text-xs">Keşfet</span>
        <div className="w-px h-6 bg-gradient-to-b from-gray-500 to-transparent" />
      </div>

      {/* Live Ticker Band — full width bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-20">
        <LiveTicker />
      </div>
    </section>
  );
}
